import React, { useState, useMemo } from 'react';
import { Form, Container, Row, Col, Button } from 'react-bootstrap';
import { toast, ToastContainer } from "react-toastify";

import { useUser } from "../../UserContext";

import "react-toastify/dist/ReactToastify.css";
import "../../../css/UpdateData.css";


function UpdateData({ columns, rows, tableName, onUpdate }) {

  const { role } = useUser(); // Ottieni il ruolo corrente

  const [selectedID, setSelectedID] = useState('');
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [newValues, setNewValues] = useState({});
  const [currentIDVersion, setCurrentIDVersion] = useState(null);

  if (!columns || columns.length === 0 || !rows) {
    return <p>No data available to update</p>;
  }

  // 🔹 Definisci le colonne modificabili in base al ruolo
  const updatableColumns = useMemo(() => {
    const base = columns.filter(col => col !== 'ID' && col !== 'ID_version');

    if (role === "admin") return base;

    if (role === "elettrico") {
      const blocked = [
        "Temperature [°C]",
        "AirPressure [bar]",
        "GasPressure_O2 [bar]",
        "GasPressure_N2 [bar]",
        "GasPressure_Air [bar]",
      ];
      return base.filter(col => !blocked.includes(col));
    }

    if (role === "meccanico") {
      const allowed = [
        "Model",
        "Version",
        "Diagram",
        "LaserType",
        "Temperature [°C]",
        "AirPressure [bar]",
        "GasPressure_O2 [bar]",
        "GasPressure_N2 [bar]",
        "GasPressure_Air [bar]",
      ];
      return base.filter(col => allowed.includes(col));
    }

    // Nessun login → niente colonne aggiornabili
    return [];
  }, [columns, role]);

  const handleColumnChange = (col, value) => {
    setNewValues(prev => ({ ...prev, [col]: value }));
  };

  const handleColumnSelect = (col) => {
    setSelectedColumns(prev =>
      prev.includes(col)
        ? prev.filter(c => c !== col)
        : [...prev, col]
    );
  };

  const handleRowSelect = (id) => {
    setSelectedID(id);
    setSelectedColumns([]);
    setNewValues({});
    const row = rows.find(r => r.ID === id);
    setCurrentIDVersion(row ? row.ID_version : null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedID) {
      toast.warning("Seleziona una riga da aggiornare (ID).");
      return;
    }
    if (selectedColumns.length === 0) {
      toast.warning("Seleziona almeno una colonna da aggiornare.");
      return;
    }

    for (let col of selectedColumns) {
      if (!newValues[col] || newValues[col].trim() === "") {
        toast.warning(`Inserisci un valore per la colonna "${col}".`);
        return;
      }
    }

    // Costruzione nuovo ID se Version o LaserType vengono aggiornati
    const currentRow = rows.find(r => r.ID === selectedID);
    const newVersion = selectedColumns.includes("Version") ? newValues["Version"] : currentRow.Version;
    const newLaserType = selectedColumns.includes("LaserType") ? newValues["LaserType"] : currentRow.LaserType;
    const newID = `${newVersion}_${newLaserType}`;
    if (newID !== selectedID) newValues["ID"] = newID;

    const formData = new URLSearchParams();
    formData.append("tableName", tableName);
    formData.append("currentID", selectedID);
    formData.append("updatedColumns", JSON.stringify(selectedColumns));
    formData.append("newValues", JSON.stringify(newValues));
    formData.append("currentIDVersion", currentIDVersion);

    const API_URL = process.env.REACT_APP_API_URL;

    try {
      const res = await fetch(`${API_URL}/updateDB`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: formData.toString(),
      });

      if (!res.ok) {
        const errData = await res.json();
        if (res.status === 409) {
          toast.error("La riga è stata modificata da un altro utente. Aggiorna la tabella e riprova.");
        } else {
          throw new Error(errData.detail || "Update failed");
        }
        return;
      }

      await res.json();
      toast.success("Riga aggiornata con successo");

      setSelectedID('');
      setSelectedColumns([]);
      setNewValues({});
      setCurrentIDVersion(null);

      if (onUpdate) onUpdate();

    } catch (err) {
      console.error(err);
      toast.error(err.message || "Errore imprevisto");
    }
  };

  // Nessun login → mostra messaggio
  if (!role) {
    return (
      <Container className="mt-5 text-center">
        <h5>Effettua il login per aggiornare i dati.</h5>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      <ToastContainer position="top-right" autoClose={3000} />
      <Form onSubmit={handleSubmit} className="update-form p-4 rounded shadow-sm bg-light">

        {/* Selezione riga */}
        <Row className="mb-4">
          <Col md={6}>
            <Form.Group>
              <Form.Label className="fw-semibold" style={{ color: "#e0a800" }}>
                Seleziona riga (ID)
              </Form.Label>
              <Form.Select
                value={selectedID}
                onChange={e => handleRowSelect(e.target.value)}
                className="custom-select"
              >
                <option value="">-- Seleziona ID --</option>
                {rows.map(r => (
                  <option key={r.ID} value={r.ID}>{r.ID}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>

        {/* Colonne modificabili */}
        <Row className="mt-2">
          {updatableColumns.map(col => (
            <Col key={col} md={3} sm={6} xs={12} className="mb-3">
              <div className="form-field p-2 rounded border bg-white">
                <Form.Check
                  type="checkbox"
                  label={<span className="fw-semibold">{col}</span>}
                  checked={selectedColumns.includes(col)}
                  onChange={() => handleColumnSelect(col)}
                />
                {selectedColumns.includes(col) && (
                  <Form.Control
                    type="text"
                    placeholder={`Nuovo valore per ${col}`}
                    value={newValues[col] || ''}
                    onChange={e => handleColumnChange(col, e.target.value)}
                    className="mt-2"
                  />
                )}
              </div>
            </Col>
          ))}
        </Row>

        {/* Bottone di aggiornamento */}
        <Row className="mt-4">
          <Col className="text-center">
            <Button
              type="submit"
              variant="warning"
              size="lg"
              className="update-btn"
              disabled={!selectedID || selectedColumns.length === 0}
            >
              AGGIORNA RIGA
            </Button>
          </Col>
        </Row>
      </Form>
    </Container>
  );
}

export default UpdateData;
